import { ExpertRequestStage, ExpertRequestTransitionAction } from '@/__generated__/graphql';

export interface StageTransition {
  action: ExpertRequestTransitionAction;
  /** Effective stage the ER lands in after the transition. */
  target: ExpertRequestStage;
  /** Verb shown in the stage dropdown / banner CTA. */
  label: string;
}

/**
 * Legal lifecycle transitions per effective stage (ENG-5290 §2.3) — the SAME
 * graph the api enforces. Drives the stage dropdown's enabled options and maps
 * each action to its confirm modal. Null-stage legacy rows are resolved via
 * effectiveStage before lookup, so the dropdown never offers a rejected target.
 */
export const STAGE_TRANSITIONS: Record<ExpertRequestStage, StageTransition[]> = {
  [ExpertRequestStage.Draft]: [
    {
      action: ExpertRequestTransitionAction.SubmitForSourcing,
      target: ExpertRequestStage.Active,
      label: 'Submit for sourcing',
    },
    // A draft can be closed directly (api legalSuccessors: draft -> {active, closed}).
    {
      action: ExpertRequestTransitionAction.Close,
      target: ExpertRequestStage.Closed,
      label: 'Close',
    },
  ],
  [ExpertRequestStage.Active]: [
    {
      action: ExpertRequestTransitionAction.PutOnHold,
      target: ExpertRequestStage.OnHold,
      label: 'Put on hold',
    },
    {
      action: ExpertRequestTransitionAction.Close,
      target: ExpertRequestStage.Closed,
      label: 'Close',
    },
  ],
  [ExpertRequestStage.OnHold]: [
    {
      action: ExpertRequestTransitionAction.Resume,
      target: ExpertRequestStage.Active,
      label: 'Resume sourcing',
    },
    {
      action: ExpertRequestTransitionAction.Close,
      target: ExpertRequestStage.Closed,
      label: 'Close',
    },
  ],
  [ExpertRequestStage.Closed]: [
    {
      action: ExpertRequestTransitionAction.Reopen,
      target: ExpertRequestStage.Active,
      label: 'Reopen request',
    },
  ],
};

export function legalTransitions(stage: ExpertRequestStage): StageTransition[] {
  return STAGE_TRANSITIONS[stage] ?? [];
}

/** Fixed display order so the dropdown teaches the lifecycle sequence (ENG-5314). */
export const STAGE_ORDER: ExpertRequestStage[] = [
  ExpertRequestStage.Draft,
  ExpertRequestStage.Active,
  ExpertRequestStage.OnHold,
  ExpertRequestStage.Closed,
];

export type StageRowStatus = 'current' | 'available' | 'locked-seq' | 'locked-perm';

export interface StageRow {
  stage: ExpertRequestStage;
  status: StageRowStatus;
  /** Present on `available` rows — the transition to fire on select. */
  action?: ExpertRequestTransitionAction;
  /** Only the Active row carries one, keyed on the CURRENT stage (ENG-5314). */
  parenthetical?: string;
}

// The Active row's parenthetical explains what "→ Active" means from where you are.
const ACTIVE_PARENTHETICAL: Partial<Record<ExpertRequestStage, string>> = {
  [ExpertRequestStage.Draft]: 'submit for sourcing',
  [ExpertRequestStage.OnHold]: 'resume sourcing',
  [ExpertRequestStage.Closed]: 'reopen',
};

/**
 * Build the full 4-row stage picker (ENG-5314): every stage always shown, in
 * order. The current stage is inert; a stage reachable by a legal transition is
 * `available` when the viewer holds the verb, else `locked-perm`; anything not
 * reachable from the current stage is `locked-seq`.
 */
export function buildStageRows(
  current: ExpertRequestStage,
  allowedActions?: readonly (string | null)[] | null
): StageRow[] {
  const legal = legalTransitions(current);
  return STAGE_ORDER.map((stage): StageRow => {
    if (stage === current) {
      return { stage, status: 'current' };
    }
    const transition = legal.find((t) => t.target === stage);
    if (!transition) {
      return { stage, status: 'locked-seq' };
    }
    const allowed = allowedActions?.includes(transition.action) ?? false;
    const parenthetical =
      stage === ExpertRequestStage.Active ? ACTIVE_PARENTHETICAL[current] : undefined;
    return {
      stage,
      status: allowed ? 'available' : 'locked-perm',
      action: transition.action,
      parenthetical,
    };
  });
}
